import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError } from "@/lib/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import { inputCls, inputClsError, Btn } from "@/dashboard/ui";
import { MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError("");
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmed)) {
      setFieldError("Please enter a valid email address.");
      return;
    }
    setFieldError("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: trimmed });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="rounded-3xl border border-neutral-200 bg-white p-8 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.12)]">
          <Link to="/" className="flex items-center justify-center gap-2.5" data-testid="forgot-brand">
            <BrandLogo />
            <span className="text-lg font-bold tracking-tight text-[#111111]">Savy Kerno</span>
          </Link>

          {done ? (
            <div className="mt-8 text-center" data-testid="forgot-success">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-[#111111] text-white">
                <MailCheck size={20} />
              </span>
              <h1 className="mt-5 text-xl font-extrabold tracking-tight text-[#111111]">Check your inbox</h1>
              <p className="mt-2 text-sm leading-relaxed text-neutral-500">
                If an account exists for that email, a reset link is on its way.
                The link expires in 30 minutes and can only be used once.
              </p>
              <Link to="/login" className="mt-6 inline-block text-sm font-semibold text-[#111111] underline underline-offset-4">
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <h1 className="mt-6 text-center text-xl font-extrabold tracking-tight text-[#111111]">Reset your password</h1>
              <p className="mt-1.5 text-center text-sm text-neutral-500">
                Enter your account email and we'll send you a secure reset link.
              </p>
              <form onSubmit={submit} noValidate className="mt-7 space-y-4" data-testid="forgot-form">
                <div>
                  <label htmlFor="forgot-email" className="mb-1.5 block text-xs font-semibold text-neutral-600">Email</label>
                  <input
                    id="forgot-email"
                    data-testid="forgot-email-input"
                    type="email"
                    className={fieldError ? inputClsError : inputCls}
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (fieldError && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e.target.value.trim())) setFieldError("");
                    }}
                    placeholder="you@business.com"
                    aria-invalid={!!fieldError || undefined}
                    aria-describedby={fieldError ? "forgot-email-error" : undefined}
                    autoComplete="email"
                  />
                  {fieldError && (
                    <p id="forgot-email-error" role="alert" data-testid="forgot-email-error" className="mt-1.5 text-xs text-red-600">{fieldError}</p>
                  )}
                </div>
                {error && (
                  <p data-testid="forgot-error" className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p>
                )}
                <Btn data-testid="forgot-submit-button" type="submit" disabled={busy} className="w-full py-3">
                  {busy ? "Sending…" : "Send Reset Link"}
                </Btn>
              </form>
              <p className="mt-5 text-center text-sm">
                <Link to="/login" className="font-medium text-neutral-500 hover:text-[#111111]">
                  ← Back to sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
